/* Dollar Labs B2B — B2B pricing on search overlay results.
 *
 * Two gaps stack up here:
 *   1. The app's card rewriter is restricted to collection/search page types,
 *      so a search overlay opened on a product page is never touched.
 *   2. This store searches through Doofinder, which renders its own DOM from
 *      its own index. That index holds retail prices and knows nothing about
 *      price lists, so no amount of app-side work reaches it.
 *
 * Bold had the same blind spot — its locators were Dawn class names that match
 * neither this theme nor Doofinder.
 *
 * So this is deliberately markup-agnostic: it finds product links inside a
 * search overlay, resolves each item's real variant, asks the app SDK for that
 * variant's B2B price, and rewrites the money text in place. Nothing here
 * depends on Doofinder's class names beyond locating the overlay root, and that
 * list can be extended at runtime via window.DollarlabsSearchOverlaySelectors
 * without editing this file.
 */
(function () {
  "use strict";

  var API = "DollarlabsGetBestPrice";
  var CLAIM = "dlSearchPriced";

  // Overlay roots. Doofinder has shipped several layer flavours (classic,
  // fullscreen, embedded), hence the wildcard matches alongside the theme's own
  // predictive-search container.
  var DEFAULT_ROOTS = [
    ".sidebar__search-results", // theme predictive search
    "[id*='doofinder' i]",
    "[class*='doofinder' i]",
    "[id*='dfclassic' i]",
    "[class*='dfd-' i]",
    "df-fullscreen",
    "df-classic",
    "df-mobile",
  ];

  // Regions whose prices belong to something else: our own blocks, the PDP's
  // buy form, and the cart (line pricing is the discount function's job).
  var FORBIDDEN =
    "[data-v3],[data-custom-price],[data-custom-price-js]," +
    ".dollarlabs_pricelist--product_price,.dollarlabs_pricelist--volume_tiers," +
    "[data-dollarlabs-rendered],form[data-product-form]," +
    "cart-form,cart-drawer,[data-js-cart-item]";

  // Currency symbol followed by a digit, or amount plus ISO code.
  var MONEY_RE = /(\p{Sc}\s?\d)|(\d[\d.,]*\s?[A-Z]{3}\b)/u;

  var variantCache = Object.create(null);

  function roots() {
    var extra = window.DollarlabsSearchOverlaySelectors;
    return DEFAULT_ROOTS.concat(
      Object.prototype.toString.call(extra) === "[object Array]" ? extra : [],
    );
  }

  function handleFromHref(href) {
    if (!href) return null;
    var m = href.split("?")[0].split("#")[0].match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  function variantFromHref(href) {
    var m = href && href.match(/[?&]variant=(\d+)/);
    return m ? m[1] : null;
  }

  // Mirrors Liquid's selected_or_first_available_variant, which is the price a
  // shopper sees when they land on the product from a search result.
  function pickVariant(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (product.variants[i].available) return product.variants[i];
    }
    return product.variants[0];
  }

  function loadVariantId(handle) {
    if (handle in variantCache) return variantCache[handle];
    var root =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
      "/";
    variantCache[handle] = fetch(
      root + "products/" + encodeURIComponent(handle) + ".js",
      { credentials: "same-origin" },
    )
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (p) {
        var v = pickVariant(p);
        return v ? String(v.id) : null;
      })
      .catch(function () {
        return null;
      });
    return variantCache[handle];
  }

  // The card = the largest ancestor still referencing only this one product,
  // so we never climb far enough to grab a neighbouring result's price.
  function cardRootFor(link, handle, stopAt) {
    var card = link;
    var el = link.parentElement;
    for (var hops = 0; el && el !== stopAt && hops < 10; hops++) {
      var single = true;
      var links = el.querySelectorAll("a[href*='/products/']");
      for (var i = 0; i < links.length; i++) {
        if (handleFromHref(links[i].getAttribute("href")) !== handle) {
          single = false;
          break;
        }
      }
      if (!single) break;
      card = el;
      el = el.parentElement;
    }
    return card;
  }

  // The price region: a class-named price container if the search app provides
  // one (so a sale/compare pair collapses to a single B2B price), else the
  // smallest money-looking element.
  function findPriceEl(card) {
    var named = card.querySelectorAll("[class*='price' i]");
    for (var n = 0; n < named.length; n++) {
      var c = named[n];
      if (c.closest(FORBIDDEN)) continue;
      var ct = (c.textContent || "").trim();
      if (ct && ct.length <= 40 && MONEY_RE.test(ct)) return c;
    }

    var best = null;
    var bestLen = Infinity;
    var all = card.querySelectorAll("*");
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.closest(FORBIDDEN)) continue;
      var t = (el.textContent || "").trim();
      if (!t || t.length > 40 || !MONEY_RE.test(t)) continue;
      if (t.length < bestLen) {
        best = el;
        bestLen = t.length;
      }
    }
    return best;
  }

  // Descend to the element that actually prints the money. Doofinder wraps the
  // amount in its own tag (<strong>/<span>), and writing textContent on the
  // container would delete that wrapper and with it the bold weight. Only
  // descends while a single child carries the whole text, so a sale/compare
  // pair still collapses on the container.
  function moneyLeaf(el) {
    var cur = el;
    for (var guard = 0; guard < 6; guard++) {
      var kids = cur.children ? Array.prototype.slice.call(cur.children) : [];
      var filled = kids.filter(function (k) {
        return (k.textContent || "").trim() !== "";
      });
      if (filled.length !== 1) break;
      if ((filled[0].textContent || "").trim() !== (cur.textContent || "").trim())
        break;
      cur = filled[0];
    }
    return cur;
  }

  function process(link, stopAt) {
    var href = link.getAttribute("href");
    var handle = handleFromHref(href);
    if (!handle) return;

    var card = cardRootFor(link, handle, stopAt);
    if (card.dataset && card.dataset[CLAIM]) return;

    var priceEl = findPriceEl(card);
    if (!priceEl || priceEl.closest(FORBIDDEN)) return;

    // Claim before awaiting so a re-entrant scan can't double-process.
    if (card.dataset) card.dataset[CLAIM] = "1";
    var originalText = (priceEl.textContent || "").trim();
    var inline = variantFromHref(href);

    Promise.resolve(inline || loadVariantId(handle))
      .then(function (variantId) {
        return variantId ? window[API](variantId, 1) : null;
      })
      .then(function (best) {
        // Only intervene when there is a real B2B discount; otherwise whatever
        // the search app printed is already the right price.
        if (!best || !best.hasDiscount || !best.formattedPrice) return;
        moneyLeaf(priceEl).textContent = best.formattedPrice;
        priceEl.setAttribute("data-dollarlabs-search-price", "1");
        if (originalText && originalText !== best.formattedPrice) {
          priceEl.setAttribute("data-dollarlabs-was", originalText);
        }
      })
      .catch(function () {
        /* leave the search app's price alone */
      });
  }

  function scan() {
    var sel = roots();
    for (var i = 0; i < sel.length; i++) {
      var containers;
      try {
        containers = document.querySelectorAll(sel[i]);
      } catch (e) {
        continue; // an invalid custom selector must not kill the pass
      }
      for (var c = 0; c < containers.length; c++) {
        var links = containers[c].querySelectorAll("a[href*='/products/']");
        for (var l = 0; l < links.length; l++) process(links[l], containers[c]);
      }
    }
  }

  function waitForApi() {
    if (typeof window[API] === "function") return Promise.resolve(true);
    return new Promise(function (resolve) {
      var waited = 0;
      var tick = setInterval(function () {
        if (typeof window[API] === "function") {
          clearInterval(tick);
          resolve(true);
        } else if ((waited += 100) >= 5000) {
          clearInterval(tick);
          resolve(false);
        }
      }, 100);
    });
  }

  function start() {
    waitForApi().then(function (ready) {
      if (!ready || !document.body) return;
      scan();

      // Search apps re-render results on every keystroke. Our own writes
      // retrigger this, but the claim flag makes the follow-up scan a no-op.
      var timer;
      new MutationObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(scan, 150);
      }).observe(document.body, { childList: true, subtree: true });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
